import type { SearchResultItem } from '@setone-sounds/shared/types';

import type { SearchResponse } from './endpoints';

const STORAGE_KEY = 'setone-ai-search-history-v2';
const MAX_ENTRIES = 40;

export interface AiHistoryEntry {
  q: string;
  at: number;
  response: SearchResponse;
}

function readRaw(): AiHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is AiHistoryEntry =>
        Boolean(e) &&
        typeof e === 'object' &&
        typeof (e as AiHistoryEntry).q === 'string' &&
        typeof (e as AiHistoryEntry).at === 'number' &&
        (e as AiHistoryEntry).response &&
        Array.isArray((e as AiHistoryEntry).response.results),
    );
  } catch {
    return [];
  }
}

function writeRaw(entries: AiHistoryEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    /* quota / private mode */
  }
}

export function listAiSearchHistory(): AiHistoryEntry[] {
  return readRaw().sort((a, b) => b.at - a.at);
}

export function getAiSearchHistory(q: string): AiHistoryEntry | null {
  const needle = q.trim().toLowerCase();
  if (!needle) return null;
  return readRaw().find((e) => e.q.trim().toLowerCase() === needle) ?? null;
}

export function saveAiSearchHistory(q: string, response: SearchResponse): void {
  const trimmed = q.trim();
  if (!trimmed) return;
  const results: SearchResultItem[] = response.results ?? [];
  const next: AiHistoryEntry = {
    q: trimmed,
    at: Date.now(),
    response: { q: response.q || trimmed, results },
  };
  const rest = readRaw().filter((e) => e.q.trim().toLowerCase() !== trimmed.toLowerCase());
  writeRaw([next, ...rest]);
}

export function removeAiSearchHistory(q: string): void {
  const needle = q.trim().toLowerCase();
  writeRaw(readRaw().filter((e) => e.q.trim().toLowerCase() !== needle));
}

export function clearAiSearchHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
